import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { EMPTY, merge, of } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { Organization } from '../client-api.service';
import { AppService } from '../shared/app.service';
import { SignatureInfo } from '../shared/signature-info';
import { SubsManager } from '../shared/subs-manager';

interface SignatureFormControls {
  name: AbstractControl;
  org: AbstractControl;
  phone: AbstractControl;
  title: AbstractControl;
  [key: string]: AbstractControl;
}
interface SignatureForm extends FormGroup {
  controls: SignatureFormControls;
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-signature-form',
    templateUrl: './signature-form.component.html',
    styles: [`
  .form-group { min-width: 230px }
`],
    standalone: false
})
export class SignatureFormComponent implements OnInit, OnDestroy {
  private readonly subsMgr = new SubsManager();

  readonly fc: SignatureFormControls = {
    name: new FormControl(undefined, [Validators.required]),
    org: new FormControl(undefined, [Validators.required]),
    phone: new FormControl(undefined, [Validators.required, Validators.pattern(/\(\d{3}\)\s{1}\d{3}-\d{4}/)]),
    title: new FormControl(undefined, [Validators.required])
  };
  readonly form: SignatureForm = new FormGroup(this.fc) as SignatureForm;

  readonly orgs$ = this.appSvc.orgs$;

  @Input()
  set signatureInfo(value: SignatureInfo | undefined) {
    this.signatureInfoToForm(value);
  }
  @Output()
  readonly signatureInfoChange = new EventEmitter<SignatureInfo | undefined>();

  constructor(private appSvc: AppService) { }

  ngOnInit() {

    // switch phones when org changes.
    this.subsMgr.addSub = this.fc.org.valueChanges.pipe(
      switchMap((orgCode) => this.appSvc.orgMap$.pipe(map(orgMap => orgMap.get(orgCode)))),
      filter(org => !!org?.primaryPhone),
      tap((org) => this.fc.phone.setValue(this.formatPhone(org?.primaryPhone)))
    ).subscribe();

    // update output when form changes.
    this.subsMgr.addSub = merge(this.form.valid ? of(null) : EMPTY, this.form.valueChanges).pipe(
      switchMap(() => this.appSvc.orgMap$),
      tap((orgMap) => {
        const signatureInfo = this.form.valid ? this.formToSignatureInfo(orgMap) : undefined;
        this.signatureInfoChange.emit(signatureInfo);
      })
    ).subscribe();
  }

  ngOnDestroy() {
    this.subsMgr.onDestroy();
  }

  private formToSignatureInfo(orgMap: Map<string | undefined, Organization>): SignatureInfo | undefined {
    const ctrls = this.form.controls;
    const orgCode = ctrls.org.value;
    const defaultOrg = orgMap.get(environment.defaultOrgCode);
    const personName = ctrls.name.value;
    const phone = ctrls.phone.value;
    const title = ctrls.title.value;
    const org = orgMap.get(orgCode);
    if (!org || !defaultOrg || typeof(personName) !== 'string' || typeof(phone) !== 'string' || typeof(title) !== 'string') {
      return undefined;
    }
    return {
      orgCode: org.orgCode || defaultOrg.orgCode || '',
      personName,
      phone,
      title
    };
  }

  private signatureInfoToForm(sig: SignatureInfo | undefined) {
    console.log('sig', sig)
    this.form.reset();
    if (sig) {
      const ctrls = this.form.controls;
      ctrls.name.setValue(sig.personName, { emitEvent: false });
      ctrls.org.setValue(sig.orgCode, { emitEvent: false });
      ctrls.phone.setValue(this.formatPhone(sig.phone), { emitEvent: false });
      ctrls.title.setValue(sig.title, { emitEvent: false });
      console.log('ctrls', ctrls)
    }
    
    this.form.updateValueAndValidity({ emitEvent: true });
  }

  private formatPhone(phone: string | undefined): string {
    if (!phone) return '';

    // Extract digits & extension
    const phoneDigits = phone.replace(/[^\d]/g, '');
    let ext = '';

    // Check for ext using common delimiters, e.g. 'ext', 'x', etc.
    const extMatch = phone.match(/(?:ext\.?|x)\s*(\d+)/i);
    if (extMatch) {
      ext = extMatch[1];
    }

    // Main number: expecting 10 digits
    let area = '', prefix = '', line = '';
    if (phoneDigits.length >= 10) {
      area = phoneDigits.slice(0, 3);
      prefix = phoneDigits.slice(3, 6);
      line = phoneDigits.slice(6, 10);
    } else {
      return phone; // fallback for non-standard lengths
    }
    
    let formatted = `(${area}) ${prefix}-${line}`;
    if (ext) {
      formatted += ` ext. ${ext}`;
    }
    return formatted;
  }
}
